import debug from 'debug'
import React, { Suspense, useEffect, useRef, useState } from 'react'
import { BrowserRouter, Link, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import './App.scss'
import { LoadState } from './common/LoadState'
import { useClassName } from './common/UseClassName'
import { TopLink } from './components/top-link/TopLink'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { AppActionType, AppProvider, useAppDispatch, useAppState } from './context/AppContext'
import { PlotsProvider } from './context/plots/PlotsContext'
import { PlotsSetup } from './context/plots/PlotsSetup'
import { UserPlotsProvider } from './context/plots/UserPlotsContext'
import { environment } from './environments/environment'
// import { AdminPage } from './pages/admin/AdminPage'
import { CommunityGuidelinesPage } from './pages/community-guidelines/community-guidelines'
import { ContactPage } from './pages/contact/ContactPage'
import { CreateLandingPage } from './pages/create-landing/create-landing'
import { CreatePage } from './pages/create/CreatePage'
// import { FaqPage } from './pages/faq/FaqPage'
import { FoundersPage } from './pages/founders/FoundersPage'
// import { HomePage } from './pages/home/HomePage'
import { LoginPage } from './pages/login/LoginPage'
import { LogoutPage } from './pages/login/LogoutPage'
import { MapsPage } from './pages/maps/MapsPage'
// import { MintPage } from './pages/mint-v2/MintPage'
import { ViewPage } from './pages/view/ViewPage'
import { WhitepaperPage } from './pages/whitepaper/WhitepaperPage'
import './services'
import { AnalyticsEventName } from './services/analytics/AnalyticsEventName'
import { mainSuite } from './services/ServiceFactory'
import { UserProvider, useUserState } from './services/user/UserContext'
import { UserServiceProvider, useUserService } from './services/user/UserServiceContext'
import versionFile from './version.json'
import { Features } from './context/FeaturesState'
import { SearchParam } from './common/SearchParam'
import { GameSiteUrlParams } from '@play-co/svs-consts';
import {CommunityPageRedirect} from './pages/community/CommunityPageRedirect'

const versionToken = 'VERSION';
const log = debug('app:app');

window.debugApp = debug;
window['log'] = debug('app:misc');

if (environment.production) {
  debug.disable();
} else {
  debug.enable('*');
}

// lazy load these pages since they're big or have lots of text
const CookiesPage = React.lazy(() => import('./pages/terms/CookiesPage'));
const Cookies20220420Page = React.lazy(() => import('./pages/terms/previous/Cookies20220420Page'));
const FoundersPassTermsPage = React.lazy(() => import('./pages/terms/FoudersPassTermsPage'));
const PrivacyNoticePage = React.lazy(() => import('./pages/terms/PrivacyNoticePage'));
const PrivacyNotice20220420Page = React.lazy(() => import('./pages/terms/previous/PrivacyNotice20220420Page'));
const PrivacyCaNoticePage = React.lazy(() => import('./pages/terms/PrivacyCaNoticePage'));
const TermsOfServicePage = React.lazy(() => import('./pages/terms/TermsOfServicePage'));
const Terms20220420Page = React.lazy(() => import('./pages/terms/previous/Terms20220420Page'));
const CopyrightPage = React.lazy(() => import('./pages/terms/CopyrightPage'));
const Copyright20220420Page = React.lazy(() => import('./pages/terms/previous/Copyright20220420Page'));
const PLOTTerms20220420Page = React.lazy(() => import('./pages/terms/previous/PLOTTerms20220420Page'));

export const ModalRootId = 'modal-root';

function LoadingSection() {
  return (
    <div className="container py-2">
      Loading...
    </div>
  );
}

function Layout() {
  const userService = useUserService();
  const appState = useAppState();
  const appDispatch = useAppDispatch();
  const location = useLocation();
  const userAccount = useUserState();
  const initialized = appState.readyState === LoadState.Loaded;
  const mainClassName = useClassName('main-con', appState.mainConFullHeight && 'full-height');

  useEffect(() => {
    if (!appState?.initialSearchParams) { return; }
    if (initialized) { return; }
    appDispatch({
      type: AppActionType.UpdateReadyState,
      readyState: LoadState.Loaded,
    });
  }, [appState]);

  useEffect(() => {
    const doc = document.documentElement;
    if (appState.mainConFullHeight) {
      doc.classList.add('full-height');
    } else {
      doc.classList.remove('full-height');
    }
  }, [appState.mainConFullHeight]);

  useEffect(() => {
    if (!appState?.initialSearchParams) { return; }

    let changed = false;
    const features: Partial<Features> = {
    };
    if (appState.initialSearchParams.has(SearchParam.SignUp)) {
      features.showContactsSection = true;
      features.showFooterSignUp = true;
      features.showSignUpModal = true;
      changed = true;
    }
    if (appState.initialSearchParams.has(SearchParam.Map) || appState.initialSearchParams.has(SearchParam.Maps)) {
      features.showMapLink = true;
      changed = true;
    }
    if (appState.initialSearchParams.has(SearchParam.Whitepaper)) {
      features.showWhitepaperLink = true;
      changed = true;
    }

    if (changed) {
      appDispatch({
        type: AppActionType.UpdateFeatures,
        features,
      });
    }
  }, [appState.initialSearchParams]);

  useEffect(() => {
    appDispatch({
      type: AppActionType.UpdateReadyState,
      readyState: LoadState.Loading,
    });

    userService.waitForInit().then(() => {
      // the very initial properties
      mainSuite.analyticsService.setUserState(userAccount);
    });

    if (!appState.initialSearchParams) {
      appDispatch({
        type: AppActionType.UpdateSearchParams,
        searchParams: location.search
      });
    }
  }, []);

  const mainRef = useRef<HTMLElement>();

  return (
    <>
      {appState.showNavbar && <Header />}
      <main ref={mainRef} className={mainClassName}>
        {initialized ? (
          <Suspense fallback={<LoadingSection />}>
            <Outlet />
          </Suspense>
        ) : (
          <LoadingSection />
        )}
      </main>
      <Footer />
      <div id={ ModalRootId } />
    </>
  );
}

function NotFound() {
  return (
    <Navigate to="/" />
  );
}

function Providers({ children }) {
  return (
    <AppProvider>
      <UserProvider>
        <UserServiceProvider>
          <PlotsProvider>
            <UserPlotsProvider>
              <PlotsSetup />
              {children}
            </UserPlotsProvider>
          </PlotsProvider>
        </UserServiceProvider>
      </UserProvider>
    </AppProvider>
  );
}

function App() {
  const basename = environment.routerBasename?.replace(versionToken, versionFile.version) ?? undefined;
  useEffect(() => {
    mainSuite.analyticsService.track(AnalyticsEventName.GameStart);
  }, []);

  const isViewerOrEditorPage = location.pathname.includes('view') || location.pathname === '/create' || location.pathname === '/';

  if (isViewerOrEditorPage) {
    document.body.style.overflow = 'hidden';
  }

  useEffect(() => {
    if (!isViewerOrEditorPage) {
      mainSuite.analyticsService.init();
    }
  }, []);

  return (
    <Providers>
      <BrowserRouter basename={basename}>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<CreatePage />} />
            <Route path="index.html" element={<CreatePage />} />
            <Route path="about" element={<CreateLandingPage />} />
            <Route path="community" element={<CommunityGuidelinesPage />} />
            {/* <Route path="old-home" element={<HomePage />} /> */}
            <Route path="login" element={<LoginPage />} />
            <Route path="logout" element={<LogoutPage />} />
            <Route path="whitepaper" element={<WhitepaperPage />} />
            <Route path="founders" element={<FoundersPage />} />
            {/* <Route path="faq" element={<FaqPage />} /> */}
            <Route path="faq" element={<Navigate to="/about#faq" />} />
            <Route path="contact" element={<ContactPage />} />
            {/* <Route path="mint" element={<MintPage />} /> */}
            <Route path="maps" element={<MapsPage />} />
            <Route path="map" element={<Navigate to="/maps" />} />
            <Route path="cookies" element={<CookiesPage />} />
            <Route path="cookies-2022-04-20" element={<Cookies20220420Page />} />
            <Route path="founderspassterms" element={<FoundersPassTermsPage />} />
            <Route path="privacy" element={<PrivacyNoticePage />} />
            <Route path="privacy-2022-04-20" element={<PrivacyNotice20220420Page />} />
            <Route path="privacy-ca" element={<PrivacyCaNoticePage />} />
            <Route path="copyright" element={<CopyrightPage />} />
            <Route path="copyright-2022-04-20" element={<Copyright20220420Page />} />
            <Route path="tos" element={<TermsOfServicePage />} />
            <Route path="tos-2022-04-20" element={<Terms20220420Page />} />
            <Route path="plots-2022-04-20" element={<PLOTTerms20220420Page />} />
            <Route path="create">
              <Route index element={<CreatePage />} />
              {/* creator ai page, should redirect to particular story */}
              <Route path={'ai'} element={<CommunityPageRedirect isMaster />}></Route>
              <Route path={`:${GameSiteUrlParams.CommunityShortcut}/ai`} element={<CommunityPageRedirect />}></Route>
              {/* creator page with community template url  */}
              <Route path={`:${GameSiteUrlParams.Template}`} element={<CreatePage />} />
              {/* creator page with wallet address and story index */}
              <Route path={`:${GameSiteUrlParams.WalletAddress}/:${GameSiteUrlParams.StoryIndex}`} element={<CreatePage />} />
            </Route>
            <Route path={`view/:${GameSiteUrlParams.WalletAddress}/:${GameSiteUrlParams.StoryId}`} element={<ViewPage />} />
            {/* viewer with ai */}
            <Route path={`view/:${GameSiteUrlParams.WalletAddress}/:${GameSiteUrlParams.StoryId}/ai`} element={<ViewPage isAi />} />
            {/* viewer with nft */}
            <Route path={`view/:${GameSiteUrlParams.WalletAddress}/:${GameSiteUrlParams.StoryId}/n`} element={<ViewPage isNFT />} />
            {/* viewer with nft and token */}
            <Route path={`view/:${GameSiteUrlParams.WalletAddress}/:${GameSiteUrlParams.StoryId}/n/:${GameSiteUrlParams.TokenID}`} element={<ViewPage isNFT />} />
            <Route path="caption-contest/alpha"
              element={<TopLink to={environment.captionContestUrl} />} />
            {/* <Route path="admin" element={<AdminPage />} /> */}
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </Providers>
  );
}

export default App;
